import { CSSProperties, ElementType, Fragment } from "react"
import { css, cx } from "@emotion/css"

import { GroupedRow } from "../lib/groups"

const styles = {
  container: css`
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
  `,
  grid: css`
    display: grid;
    grid-gap: 1px;
    grid-template-rows: repeat(var(--r), 36px) 20px 20px;
    grid-template-columns: min-content repeat(var(--c), minmax(80px, 200px));
  `,
  cell: css`
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
  `,
  nameCell: css`
    justify-content: flex-end;
    padding-right: 1rem;
  `,
  valueCell: css`
    background-color: var(--primary-main, #00b1a4);
  `,
  labelCell: css`
    align-items: flex-start;
  `,
  bottomLabel: css`
    grid-column: 2 / span var(--c);
  `,
  text: css`
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  `,
  nameText: css`
    color: var(--dark-grey);
    font-size: 13px;
    line-height: 1;
  `,
  valueText: css`
    color: var(--white, #fff);
    font-size: 12px;
    line-height: 14px;
    text-align: center;
  `,
  labelText: css`
    color: var(--dark-grey);
    font-size: 12px;
    line-height: 14px;
  `,
}

export type HeatGridProps = {
  keys: string[]
  rows: GroupedRow[]
  getColor: (val: number) => string
  nanColor?: string
  //
  getName?: (row: GroupedRow) => string
  NameCell?: ElementType<{ row: GroupedRow; name: string }>
  //
  getValue?: (key: string, row: GroupedRow) => number
  formatValue?: (value: number) => string
  ValueCell?: ElementType<{ row: GroupedRow; value: number }>
  //
  getLabel?: (key: string) => string
  //
  chartLabel: string
}

export function HeatGrid(props: HeatGridProps) {
  const {
    keys,
    rows = [],
    //
    getColor,
    nanColor = "#e0e0e0",
    //
    getName,
    NameCell,
    //
    getValue,
    formatValue,
    ValueCell,
    //
    getLabel,
    chartLabel,
  } = props

  const number_of_rows = rows.length
  const number_of_columns = keys.length
  const style = {
    "--r": number_of_rows,
    "--c": number_of_columns,
  } as CSSProperties

  return (
    <div className={styles.container} style={style}>
      <div className={cx(styles.grid)}>
        {rows.map((row, rowIndex) => {
          const { group, values } = row
          const name = getName?.(row) ?? group
          return (
            <Fragment key={group + rowIndex}>
              <div className={cx(styles.cell, styles.nameCell)}>
                {NameCell ? (
                  <NameCell row={row} name={name} />
                ) : (
                  <span className={cx(styles.text, styles.nameText)}>
                    {name}
                  </span>
                )}
              </div>
              {keys.map((key) => {
                const value = getValue?.(key, row) ?? values[key] ?? 0
                const color =
                  value === 0 || isNaN(value) ? nanColor : getColor(value)
                return (
                  <div
                    key={group + key}
                    className={cx(styles.cell, styles.valueCell)}
                    style={{ backgroundColor: color }}
                  >
                    {ValueCell ? (
                      <ValueCell row={row} value={value} />
                    ) : (
                      <span className={cx(styles.text, styles.valueText)}>
                        {formatValue?.(value) ?? value.toString()}
                      </span>
                    )}
                  </div>
                )
              })}
            </Fragment>
          )
        })}
        <div />
        {keys.map((key) => {
          const label = getLabel?.(key) ?? key
          return (
            <div
              key={`key_${key}`}
              className={cx(styles.cell, styles.labelCell)}
            >
              <span className={cx(styles.text, styles.labelText)}>{label}</span>
            </div>
          )
        })}
        <div className={cx(styles.cell, styles.labelCell, styles.bottomLabel)}>
          <span className={cx(styles.text, styles.labelText)}>
            {chartLabel}
          </span>
        </div>
      </div>
      {/* grid */}
    </div>
  )
}

export type ColorScaleProps = {
  minLabel: string
  maxLabel: string
  colors: string[]
  className?: string
}

export function ColorScale(props: ColorScaleProps) {
  const { minLabel, maxLabel, colors, className } = props
  const style = {
    "--cs": colors.length,
  } as CSSProperties
  return (
    <div
      style={style}
      className={cx(
        css`
          display: grid;
          grid-template-columns: auto repeat(var(--cs), 16px) auto;
          align-items: center;
          font-size: 11px;
          .minlabel {
            padding-inline-end: 8px;
          }
          .maxlabel {
            padding-inline-start: 8px;
          }
        `,
        className,
      )}
    >
      <div className="minlabel">
        <span>{minLabel}</span>
      </div>
      {colors.map((c, idx) => (
        <div
          key={c + idx}
          style={{ background: c }}
          className={css`
            height: 12px;
          `}
        />
      ))}
      <div className="maxlabel">
        <span>{maxLabel}</span>
      </div>
    </div>
  )
}
